import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import skillsData from '../../data/skills.json';
import Planet from './Planet';
import PlanetZoomView from './PlanetZoomView';

// Import CSS modules
import '../../styles/about/3d.css';
import '../../styles/about/animations.css';
import '../../styles/about/depth.css';
import '../../styles/about/planet-mode.css';
import '../../styles/about/planets.css';
import '../../styles/about/moon.css';

interface SkillCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  technologies: string[];
  position: { x: number; y: number; z: number };
  planetMode: {
    radius: number;
    orbitRadius: number;
    rotationSpeed: number;
    texture: string;
    atmosphere: {
      color: string;
      intensity: number;
    };
    moons: Array<{
      name: string;
      orbitRadius: number;
      size: number;
      speed: number;
    }>;
  };
  color: string;
  level: number;
  influences: string[];
  dependencies: string[];
  connections: Array<{
    targetId: string;
    strength: number;
    type: 'influence' | 'dependency' | 'synergy' | 'orbit';
  }>;
}

interface PlanetModeProps {
  isActive: boolean;
  onToggleMode: () => void;
}

const PlanetMode: React.FC<PlanetModeProps> = ({ isActive, onToggleMode }) => {
  const [focusedPlanet, setFocusedPlanet] = useState<string | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract skills data
  const skills: SkillCategory[] = skillsData.data.skillCategories as SkillCategory[];

  // Calculate planet positions in 3D space
  const calculatePlanetPosition = (skill: SkillCategory, index: number, total: number) => {
    const { orbitRadius } = skill.planetMode;
    
    if (skill.id === 'data-systems') {
      // Central star position
      return { x: 0, y: 0, z: 0 };
    }

    // Calculate orbital position
    const angle = (index / total) * 2 * Math.PI;
    const x = Math.cos(angle) * (orbitRadius * 100); // Scale for viewport
    const y = Math.sin(angle) * (orbitRadius * 80);
    const z = Math.sin(angle * 0.5) * 50; // Add some depth variation

    return { x, y, z };
  };

  const handlePlanetClick = (planetId: string) => {
    if (focusedPlanet === planetId) {
      // If clicking the same planet, zoom out
      handleZoomOut();
    } else {
      // Focus on the new planet
      setFocusedPlanet(planetId);
      setIsZoomed(true);
    }
  };

  const handleZoomOut = () => {
    setIsZoomed(false);
    setFocusedPlanet(null);
  };

  // Get size class based on planet importance
  const getPlanetSizeClass = (skill: SkillCategory): string => {
    if (skill.id === 'data-systems') return 'planet--size-xlarge';
    if (skill.level >= 90) return 'planet--size-large';
    if (skill.level >= 80) return 'planet--size-medium';
    return 'planet--size-small';
  };

  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.classList.add('planet-mode-enter');
    }
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="planet-mode stacking-context--planet-mode" ref={containerRef}>
      {/* Mode Controls */}
      <div className="mode-controls depth-layer-ui">
        <button
          className="mode-toggle-button mode-toggle-button--constellation-mode"
          onClick={onToggleMode}
        >
          <span className="mode-toggle-icon">🌌</span>
          Constellation Mode
        </button>
      </div>

      {/* Planet Mode Status */}
      <div className={`mode-status depth-layer-ui ${isActive ? 'mode-status--visible' : ''}`}>
        <div className="mode-status__title">Planet Mode Active</div>
        <div className="mode-status__description">
          {isZoomed 
            ? `Exploring ${skills.find(s => s.id === focusedPlanet)?.name || 'Unknown'} system`
            : 'Click any planet to explore its technology moons'
          }
        </div>
      </div>

      {/* Main Planet Viewport */}
      <div className={`planet-viewport ${isZoomed ? 'planet-viewport--zoomed' : ''}`}>
        <div className="planet-mode-container">
          <div className="planet-space">
            
            {/* Solar System Center (only visible when not zoomed) */}
            {!isZoomed && (
              <div className="solar-system-center depth-layer-space"></div>
            )}

            {/* Main Planets - Show only focused planet when zoomed */}
            <div className="planet-solar-system depth-layer-planets">
              {skills.map((skill, index) => {
                const position = calculatePlanetPosition(skill, index, skills.length);
                const sizeClass = getPlanetSizeClass(skill);
                const isFocused = skill.id === focusedPlanet;
                
                // When zoomed, only show the focused planet
                if (isZoomed && !isFocused) {
                  return null;
                }
                
                return (
                  <motion.div
                    key={skill.id}
                    className="planet-system depth-interactive"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ 
                      opacity: 1, 
                      scale: 1,
                      translateX: isFocused && isZoomed ? 0 : position.x,
                      translateY: isFocused && isZoomed ? 0 : position.y,
                      translateZ: isFocused && isZoomed ? 0 : position.z
                    }}
                    transition={{ 
                      duration: isZoomed ? 1.2 : 0.8,
                      delay: isZoomed ? 0 : index * 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      zIndex: isFocused && isZoomed ? 1000 : 'auto'
                    }}
                  >
                    <Planet
                      skill={skill}
                      className={`${sizeClass} planet--${skill.id.replace('_', '-')} ${isFocused && isZoomed ? 'planet--solo-focus' : ''}`}
                      onClick={() => handlePlanetClick(skill.id)}
                      isFocused={isFocused && isZoomed}
                    />
                  </motion.div>
                );
              })}
            </div>

            {/* Orbit Paths Visualization (only visible when not zoomed) */}
            {!isZoomed && skills.map((skill) => {
              if (skill.id === 'data-systems') return null;
              const orbitRadius = skill.planetMode.orbitRadius * 100;
              
              return (
                <div
                  key={`orbit-${skill.id}`}
                  className="orbit-path orbit-path--visible depth-layer-space"
                  style={{
                    width: orbitRadius * 2,
                    height: orbitRadius * 2
                  }}
                />
              );
            })}

          </div>
        </div>
      </div>

      {/* Planet Zoom View Overlay (for exit button and info panel) */}
      <AnimatePresence>
        {isZoomed && focusedPlanet && (
          <PlanetZoomView
            focusedSkill={skills.find(s => s.id === focusedPlanet)!}
            surroundingPlanets={[]} // No surrounding planets in solo focus mode
            onZoomOut={handleZoomOut}
            onSurroundingPlanetClick={() => {}} // Not used in solo focus mode
            onPlanetPosition={() => ({ x: 0, y: 0, z: 0 })} // Not used in solo focus mode
          />
        )}
      </AnimatePresence>

      {/* Planet Mode Instructions (visible when not zoomed) */}
      {!isZoomed && (
        <div className="planet-mode-instructions planet-mode-instructions--visible depth-layer-ui">
          <div className="planet-instruction-primary">
            Explore the Skills Universe
          </div>
          <div className="planet-instruction-secondary">
            Click any planet to focus on it and discover its technology moons floating in orbit
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanetMode; 